import React from 'react';
import { toast } from 'react-toastify';

import { LinkButton, Text, Icon } from '@nicheinc/campus-ds';

import * as S from './Toast.styles';

export type ToastPropsType = {
	message?: string;
	title?: string;
	link?: string;
	linkText?: string;
	linkClick?: () => void;
	loading?: boolean;
	type: 'info' | 'success' | 'warning' | 'error';
	inlineLink?: boolean;
	showIcon?: boolean;
	textColor?: string;
	hasCloseButton?: boolean;
};

const ToastComponent = ({
	message,
	type,
	title,
	link,
	linkText,
	linkClick,
	loading,
	inlineLink = false,
	showIcon = true,
	textColor,
	hasCloseButton,
}: ToastPropsType) => {
	const icon = {
		info: 'info',
		success: 'checkCircle',
		warning: 'warning',
		error: 'warningCircle',
	};

	const computedTextColor = {
		info: 'system.text.default',
		success: 'success.text.default',
		warning: 'warning.text.default',
		error: 'error.text.default',
	};

	const iconColor = {
		info: 'primary.icon.strong',
		success: 'success.icon.default',
		warning: 'warning.icon.default',
		error: 'error.icon.default',
	};

	return (
		<S.ToastWrapper type={type} hasCloseButton={hasCloseButton}>
			{showIcon && (
				<S.ToastIconWrapper>
					<Icon iconName={icon[type]} color={iconColor[type]} />
				</S.ToastIconWrapper>
			)}
			<S.ToastContentWrapper>
				{(!!title || !!message) && (
					<S.ToastTextWrapper inlineLink={inlineLink}>
						{!!title && (
							<Text
								fontWeight="medium"
								lineHeight="large"
								size="small"
								color={textColor || computedTextColor[type]}
							>
								{title}
							</Text>
						)}

						{!!link && !message && (
							<LinkButton href={link} disabled={loading}>
								{linkText || link}
							</LinkButton>
						)}

						{!!linkClick && !message && (
							<LinkButton onClick={linkClick} disabled={loading}>
								{linkText || link}
							</LinkButton>
						)}

						{!!message && !link && (
							<Text
								size="small"
								color={textColor || computedTextColor[type]}
								lineHeight="large"
							>
								{message}
							</Text>
						)}
					</S.ToastTextWrapper>
				)}
			</S.ToastContentWrapper>
		</S.ToastWrapper>
	);
};

const showToast = ({
	message,
	type,
	title,
	link,
	linkText,
	toastId,
	linkClick = undefined,
	inlineLink = false,
	autoClose = 5000,
	textColor,
	showIcon = true,
	onClose = undefined,
	onOpen = undefined,
	closeButton = false,
}: {
	message: string;
	type: ToastPropsType['type'];
	title?: string;
	link?: string;
	linkText?: string;
	linkClick?: () => void;
	inlineLink?: boolean;
	autoClose?: false | number;
	toastId?: string;
	textColor?: string;
	showIcon?: boolean;
	onClose?: (reason?: boolean | string) => void;
	onOpen?: () => void;
	closeButton?: boolean;
}) => {
	return toast(
		<ToastComponent
			message={message}
			type={type}
			title={title}
			link={link}
			linkText={linkText}
			inlineLink={inlineLink}
			linkClick={linkClick}
			showIcon={showIcon}
			textColor={textColor}
			hasCloseButton={closeButton}
		/>,
		{
			closeButton: closeButton,
			autoClose: autoClose,
			toastId: toastId,
			onClose: (reason?: boolean | string) => {
				if (onClose) {
					onClose(reason);
				}
			},
			onOpen: () => {
				if (onOpen) {
					onOpen();
				}
			},
		},
	);
};

const dismissToast = (toastId?: string) => {
	if (toastId) {
		toast.dismiss(toastId);
		return;
	}

	toast.dismiss();
};

export { showToast, ToastComponent, dismissToast };
